import { z } from 'zod';
import { cents, dateKey, optionalCents, optionalText, paginationQuery, quantity } from './common';
import { PAYMENT_METHOD_TYPES } from '../settings';
import { DELIVERY_TYPES, ORDER_STATUSES } from '../orders';

/** Filtros de pagamento e de entrega da lista de pedidos (GET /orders). */
export const ORDER_PAYMENT_FILTERS = ['paid', 'awaiting', 'due'] as const;
export type OrderPaymentFilter = (typeof ORDER_PAYMENT_FILTERS)[number];
export const ORDER_DELIVERY_FILTERS = ['pickup', 'local', 'shipping'] as const;
export type OrderDeliveryFilter = (typeof ORDER_DELIVERY_FILTERS)[number];

export const orderItemInputSchema = z.object({
  productId: z.string().uuid().nullish(),
  variantId: z.string().uuid().nullish(),
  /** Obrigatório para itens avulsos (sem produto cadastrado). */
  name: z.string().trim().max(160).optional(),
  quantity,
  /** Preço unitário praticado. Se ausente, usa o preço do produto. */
  unitPriceCents: optionalCents,
  notes: optionalText(300),
});
export type OrderItemInput = z.infer<typeof orderItemInputSchema>;

export const orderPaymentInputSchema = z.object({
  method: z.enum(PAYMENT_METHOD_TYPES),
  methodId: z.string().max(40).nullish(),
  amountCents: cents,
  /** Dinheiro: valor entregue pelo cliente (para calcular troco). */
  receivedCents: optionalCents,
  installments: z.number().int().min(1).max(24).default(1),
  reference: optionalText(120),
});
export type OrderPaymentInput = z.infer<typeof orderPaymentInputSchema>;

export const deliveryAddressSchema = z.object({
  zipCode: optionalText(9),
  street: optionalText(120),
  number: optionalText(20),
  complement: optionalText(60),
  district: optionalText(80),
  city: optionalText(80),
  state: optionalText(2),
  reference: optionalText(120),
  zoneId: optionalText(40),
});
export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>;

export const orderDiscountSchema = z
  .object({ type: z.enum(['percent', 'fixed']), value: z.number().min(0).max(1_000_000_000) })
  .nullish();

/**
 * Criação de venda (PDV) ou pedido manual.
 * - `complete: true` (padrão no PDV): registra pagamentos e conclui a venda.
 * - `complete: false`: salva como pedido em aberto (ex.: encomenda).
 */
export const createOrderSchema = z.object({
  clientRequestId: z.string().min(8).max(64).optional(),
  source: z.enum(['pos', 'manual']).default('pos'),
  complete: z.boolean().default(true),
  customerId: z.string().uuid().nullish(),
  sellerId: z.string().uuid().nullish(),
  items: z.array(orderItemInputSchema).min(1, 'Adicione pelo menos um item').max(500),
  discount: orderDiscountSchema,
  discountReason: optionalText(200),
  couponCode: optionalText(40),
  deliveryType: z.enum(DELIVERY_TYPES).default('none'),
  deliveryFeeCents: cents.default(0),
  deliveryAddress: deliveryAddressSchema.nullish(),
  deliveryNotes: optionalText(300),
  scheduledFor: z.string().datetime().nullish(),
  notes: optionalText(1000),
  payments: z.array(orderPaymentInputSchema).max(10).default([]),
  /** Condicional ("malinha"): peças saem para a cliente provar; exige cliente. */
  conditional: z.object({ dueAt: z.string().datetime().nullish() }).nullish(),
  /** Venda criada offline e sincronizada depois. */
  createdOfflineAt: z.string().datetime().optional(),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
/** Formato que o cliente ENVIA (antes de defaults/transformações do Zod). */
export type CreateOrderRequest = z.input<typeof createOrderSchema>;
export type OrderPaymentRequest = z.input<typeof orderPaymentInputSchema>;

export const returnItemsSchema = z.object({
  items: z
    .array(z.object({ orderItemId: z.string().uuid(), quantity: z.number().positive().max(1_000_000) }))
    .min(1)
    .max(200),
  /** credit = vale-troca no saldo da cliente; cash = devolução em dinheiro; none = só estoque */
  refund: z.enum(['credit', 'cash', 'none']).default('credit'),
  restock: z.boolean().default(true),
  reason: optionalText(300),
});
export type ReturnItemsInput = z.infer<typeof returnItemsSchema>;

export const closeConditionalSchema = z.object({
  /** Peças devolvidas pela cliente (voltam ao estoque). O restante vira venda. */
  returned: z
    .array(z.object({ orderItemId: z.string().uuid(), quantity: z.number().positive().max(1_000_000) }))
    .max(200)
    .default([]),
  payments: z.array(orderPaymentInputSchema).max(10).default([]),
  discount: orderDiscountSchema,
});
export type CloseConditionalInput = z.infer<typeof closeConditionalSchema>;

export const syncOrdersSchema = z.object({
  orders: z.array(createOrderSchema.extend({ clientRequestId: z.string().min(8).max(64) })).min(1).max(100),
});

export const orderListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  view: z.enum(['orders', 'sales', 'conditional', 'all']).default('all'),
  status: z.enum(ORDER_STATUSES).optional(),
  statuses: z.string().max(200).optional(),
  source: z.enum(['pos', 'catalog', 'manual', 'api']).optional(),
  paymentStatus: z.enum(['unpaid', 'partial', 'paid', 'refunded']).optional(),
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES).optional(),
  customerId: z.string().uuid().optional(),
  sellerId: z.string().uuid().optional(),
  from: dateKey.optional(),
  to: dateKey.optional(),
  sort: z.enum(['recent', 'oldest', 'total_desc', 'total_asc']).default('recent'),
  /**
   * Situação do pagamento na lista de pedidos: `paid` pago; `awaiting` aguardando o pagamento pelo
   * site (Pix, cartão ou boleto); `due` a receber na entrega, na retirada ou combinado.
   */
  payment: z.enum(ORDER_PAYMENT_FILTERS).optional(),
  /** Forma de entrega: `pickup` retirada (ou retirado na hora), `local` entrega própria/motoboy, `shipping` envio (Correios). */
  delivery: z.enum(ORDER_DELIVERY_FILTERS).optional(),
});
export type OrderListQuery = z.infer<typeof orderListQuery>;

/**
 * PATCH /orders/:id/shipping: transportadora e código de rastreio do envio.
 * O código é guardado sem espaços e em maiúsculas (ex.: "AA123456789BR"). Os dois vazios apagam o rastreio.
 */
export const orderShippingSchema = z.object({
  carrier: optionalText(60),
  trackingCode: z
    .string()
    .trim()
    .max(60)
    .nullish()
    .transform((v) => (v ? v.replace(/\s+/g, '').toUpperCase() : null))
    .refine((v) => v === null || /^[A-Z0-9][A-Z0-9./-]{3,39}$/.test(v), 'Use de 4 a 40 letras e números no código de rastreio.'),
});
export type OrderShippingInput = z.infer<typeof orderShippingSchema>;

export const orderStatusSchema = z.object({
  status: z.enum(ORDER_STATUSES),
  message: optionalText(300),
});

export const cancelOrderSchema = z.object({
  reason: optionalText(300),
  restock: z.boolean().default(true),
  refundPayments: z.boolean().default(true),
});

export const updateOrderSchema = z.object({
  notes: optionalText(1000),
  deliveryType: z.enum(DELIVERY_TYPES).optional(),
  deliveryFeeCents: cents.optional(),
  deliveryAddress: deliveryAddressSchema.nullish(),
  deliveryNotes: optionalText(300),
  scheduledFor: z.string().datetime().nullish(),
  customerId: z.string().uuid().nullish(),
});

export const addPaymentsSchema = z.object({
  payments: z.array(orderPaymentInputSchema).min(1).max(10),
  complete: z.boolean().default(false),
});

// ---------------------------------------------------------------- Cupons
export const couponInputSchema = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .min(3, 'Mínimo de 3 caracteres')
      .max(30)
      .regex(/^[A-Z0-9_-]+$/, 'Use apenas letras, números, - e _'),
    description: optionalText(200),
    discountType: z.enum(['percent', 'fixed', 'free_shipping']),
    discountValue: z.number().int().min(0).max(1_000_000_000).default(0),
    minOrderCents: optionalCents,
    maxDiscountCents: optionalCents,
    maxRedemptions: z.number().int().min(1).max(1_000_000).nullish(),
    maxRedemptionsPerCustomer: z.number().int().min(1).max(1000).nullish(),
    startsAt: z.string().datetime().nullish(),
    endsAt: z.string().datetime().nullish(),
    isActive: z.boolean().default(true),
    channel: z.enum(['all', 'catalog', 'pos']).default('all'),
    scope: z.enum(['all', 'categories', 'products']).default('all'),
    categoryIds: z.array(z.string().uuid()).max(200).default([]),
    productIds: z.array(z.string().uuid()).max(500).default([]),
    firstPurchaseOnly: z.boolean().default(false),
    showInCatalog: z.boolean().default(false),
  })
  .refine((c) => c.discountType !== 'percent' || (c.discountValue >= 1 && c.discountValue <= 100), {
    message: 'Percentual entre 1 e 100',
    path: ['discountValue'],
  })
  .refine((c) => c.discountType !== 'fixed' || c.discountValue > 0, {
    message: 'Informe o valor do desconto',
    path: ['discountValue'],
  });
export type CouponInput = z.infer<typeof couponInputSchema>;

export const validateCouponSchema = z.object({
  code: z.string().trim().min(1).max(40),
  items: z
    .array(
      z.object({
        productId: z.string().uuid().nullish(),
        variantId: z.string().uuid().nullish(),
        quantity,
        unitPriceCents: optionalCents,
      }),
    )
    .min(1)
    .max(500),
  customerPhone: optionalText(30),
  deliveryFeeCents: cents.default(0),
});
export type ValidateCouponInput = z.infer<typeof validateCouponSchema>;
